import React, { useState, useEffect, useRef } from 'react';
import GN_Icon from '../../Media/Icons/TopicsIcons/GN_Icon.svg';
import Research_Icon from '../../Media/Icons/TopicsIcons/Research_Icon.svg';
import Crime_Icon from '../../Media/Icons/TopicsIcons/Crime_Icon.svg';
import Fraud_Icon from '../../Media/Icons/TopicsIcons/Fraud_Icon.svg';
import Politics_Icon from '../../Media/Icons/TopicsIcons/Politics_Icon.svg';
import Initiatives_Icon from '../../Media/Icons/TopicsIcons/Initiatives_Icon.svg';
import Opinions_Icon from '../../Media/Icons/TopicsIcons/Opinions_Icon.svg';
import Health_Icon from '../../Media/Icons/TopicsIcons/icons8-health-80.png';
import Kids_Icon from '../../Media/Icons/TopicsIcons/icons8-kids-64.png';

import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import UnknownAuthority from '../../Media/Icons/UnknownAuthority.webp';
import Anonymous from '../../Media/Icons/anonymous.png';
import Placeholder from '../../Media/Team/placeholder.png';
import { Search, Heart, MessageCircle, Share2, Gauge, Clock, Filter, ChevronLeft, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import { isHttpUrl } from '../../utils/url';
import SharingTab from './SharingTab';
import Header from '../Header/Header';
import { useData } from '../../DataContext';
import CardDescription from './CardDescription';
import { BACKEND_URL, WEB_BASE_URL, mediaUrl, profilePicUrl } from '../../config';
import RiskoMeter from '../RiskoMeter/RiskoMeter';

type TopicsType = {
	name: string;
	img: string;
	filter: boolean;
	key: number;
};

// Define MediaType and PostType
type MediaType = {
	format: string;
	internalURL: string;
	latitude: string;
	longitude: string;
	link?: string | null;
};

type PostType = {
	id: number;
	title: string;
	body?: string;
	media: MediaType[];
	isAnonymous: boolean;
	updatedAt: string;
	createdAt: string;
	commentCount: number;
	likeCount: number;
	tags: string;
	isLiked: number;
	shareHash?: string;
	user: { name: string; id: number; profilepicURL: string };
};

export type AllPostDataType = {
	id: number;
	shareHash?: string;
	title: string;
	content?: string;
	alteredContent: string;
	isImage: boolean;
	format: string;
	image_url: any;
	media_filename: string;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	likeCount: number;
	tags: string;
	profilepicURL: string;
	isLiked: number;
	webLink: string;
	youtubeLink: string;
	isAnonymous: boolean;
};

const PostPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-32 h-32 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const PostImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);
	// Only start fetching once the card scrolls into view.
	// This is what gives strict top-to-bottom image loading: as the user
	// scrolls, each card's observer fires in DOM order, so the browser
	// issues fetches in that same order instead of all 10 racing at once.
	const [shouldLoad, setShouldLoad] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (shouldLoad) return;
		const el = containerRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '300px 0px' }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [shouldLoad]);

	if (hasError)
		return (
			<div className={`${className || ''} aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100`}>
				<PostPlaceholderSVG />
			</div>
		);

	return (
		<div
			ref={containerRef}
			className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100 ${className || ''}`}
		>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
					<PostPlaceholderSVG />
				</div>
			)}
			{shouldLoad && (
				<img
					src={src}
					alt={alt}
					decoding='async'
					className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
					onLoad={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			)}
		</div>
	);
};

const ProfileImage = ({
	name,
	pfpURL,
	className,
	isAnonymous,
}: {
	name: string;
	pfpURL?: string | null;
	className?: string;
	isAnonymous?: boolean;
}) => {
	const isAuth = name.toLowerCase().includes('authority');
	const [hasError, setHasError] = useState(false);

	// Initial src decision
	const src = isAnonymous
		? Anonymous
		: !hasError && pfpURL && pfpURL !== 'null' && pfpURL !== ''
			? profilePicUrl(pfpURL)
			: isAuth
				? UnknownAuthority
				: DefaultPFP;

	return <img src={src} alt={name} className={className} onError={() => setHasError(true)} />;
};

function MyNewsFeed() {
	const navigate = useNavigate();
	const { postMetadata, updatePostMetadata } = useData();

	const [Topics] = useState<TopicsType[]>([
		{ name: 'General News', img: GN_Icon, filter: false, key: 1 },
		{ name: 'Research', img: Research_Icon, filter: false, key: 2 },
		{ name: 'Crime', img: Crime_Icon, filter: false, key: 3 },
		{ name: 'Fraud', img: Fraud_Icon, filter: false, key: 4 },
		{ name: 'Politics', img: Politics_Icon, filter: false, key: 5 },
		{ name: 'Initiatives', img: Initiatives_Icon, filter: false, key: 6 },
		{ name: 'Opinions', img: Opinions_Icon, filter: false, key: 7 },
		{ name: 'Health', img: Health_Icon, filter: false, key: 8 },
		{ name: 'Kidjiu', img: Kids_Icon, filter: false, key: 9 },
	]);

	const [Topic, set_Topic] = useState<number>(() => {
		const savedState = sessionStorage.getItem('newsFeedState');
		if (savedState) {
			try {
				return JSON.parse(savedState).Topic;
			} catch (e) {}
		}
		return 0;
	}); // 0 = "All Topics"

	const [AllPostData, set_AllPostData] = useState<AllPostDataType[]>(() => {
		const savedState = sessionStorage.getItem('newsFeedState');
		if (savedState) {
			try {
				return JSON.parse(savedState).AllPostData || [];
			} catch (e) {}
		}
		return [];
	});
	const [likes, setLikes] = useState<Record<number, boolean>>(() => {
		const savedStateStr = sessionStorage.getItem('newsFeedState');
		if (savedStateStr) {
			try {
				const savedState = JSON.parse(savedStateStr);
				if (savedState.AllPostData) {
					return savedState.AllPostData.reduce(
						(acc: Record<number, boolean>, post: AllPostDataType) => {
							acc[post.id] = post.isLiked === 1;
							return acc;
						},
						{} as Record<number, boolean>
					);
				}
			} catch (e) {}
		}
		return {};
	});
	const [isLoading, setIsLoading] = useState(false);
	const [openShareId, setOpenShareId] = useState<number | null>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
		const savedState = sessionStorage.getItem('newsFeedState');
		if (savedState) {
			try {
				const parsed = JSON.parse(savedState);
				if (parsed.isSidebarOpen !== undefined) return parsed.isSidebarOpen;
			} catch (e) {}
		}
		return window.innerWidth >= 1024;
	});
	const shareDropdownRef = useRef<HTMLDivElement>(null);
	const wasRestoredRef = useRef<boolean>(false);

	const [searchQuery, setSearchQuery] = useState<string>('');
	// Tracks the search term the server is currently filtering on.
	// Updated only after debounce so the URL we send matches the user's intent.
	const activeSearchRef = useRef<string>('');
	// Monotonic request id — used to discard stale fetch responses if a newer
	// request (e.g. after the user types) has already started.
	const requestIdRef = useRef<number>(0);
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isFirstSearchEffect = useRef<boolean>(true);
	const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
	const [riskoMeterMediaId, setRiskoMeterMediaId] = useState<string | null>(null);
	const [toastMessage, setToastMessage] = useState<string | null>(null);
	const [page, setPage] = useState<number>(() => {
		const savedState = sessionStorage.getItem('newsFeedState');
		if (savedState) {
			try {
				return JSON.parse(savedState).page || 1;
			} catch (e) {}
		}
		return 1;
	});
	const [hasMore, setHasMore] = useState<boolean>(() => {
		const savedState = sessionStorage.getItem('newsFeedState');
		if (savedState) {
			try {
				return JSON.parse(savedState).hasMore !== false;
			} catch (e) {}
		}
		return true;
	});
	const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
	const [showScrollToOpen, setShowScrollToOpen] = useState(false);
	const lastScrollY = useRef(0);
	const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const topRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const checkVisibility = () => {
			const currentScrollY = window.scrollY;
			
			let isScrollingDown = false;
			let isScrollingUp = false;
			
			if (currentScrollY > lastScrollY.current) {
				isScrollingDown = true;
			} else if (currentScrollY < lastScrollY.current) {
				isScrollingUp = true;
			}
			
			lastScrollY.current = currentScrollY;

			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}

			let willShowTop = false;
			if (currentScrollY <= 50) {
				setShowScrollToOpen(false);
			} else if (isScrollingUp) {
				setShowScrollToOpen(true);
				willShowTop = true;
			} else if (isScrollingDown) {
				setShowScrollToOpen(false);
			}

			if (willShowTop) {
				hideTimeoutRef.current = setTimeout(() => {
					setShowScrollToOpen(false);
				}, 2000);
			}
		};

		window.addEventListener('scroll', checkVisibility);
		window.addEventListener('resize', checkVisibility);

		const resizeObserver = new ResizeObserver(checkVisibility);
		resizeObserver.observe(document.body);

		checkVisibility();
		const timer = setTimeout(checkVisibility, 100);

		return () => {
			window.removeEventListener('scroll', checkVisibility);
			window.removeEventListener('resize', checkVisibility);
			resizeObserver.disconnect();
			clearTimeout(timer);
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, []);

	// Scroll restoration using rAF polling.
	// IMPORTANT: removeItem is deferred inside tryScroll so that a mid-loop
	// data update (re-render) doesn't wipe the target before we've reached it.
	useEffect(() => {
		const savedScrollStr = sessionStorage.getItem('newsScrollPos');
		if (!savedScrollStr) return;
		const target = parseInt(savedScrollStr, 10);
		// Prevent browser from fighting us
		if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
		let rafId: number;
		let attempts = 0;
		const tryScroll = () => {
			attempts++;
			const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
			if (maxScroll >= target || attempts > 300) {
				// Clear the saved position only after we are actually scrolling to it
				sessionStorage.removeItem('newsScrollPos');
				window.scrollTo({ top: target, behavior: 'instant' as ScrollBehavior });
				// No further rAF — we're done
			} else {
				rafId = requestAnimationFrame(tryScroll);
			}
		};
		rafId = requestAnimationFrame(tryScroll);
		return () => cancelAnimationFrame(rafId);
	}, [AllPostData]);

	// Auto-refresh interval (30 seconds).
	// Only refreshes metadata (likes/comments) for already-loaded posts —
	// never replaces AllPostData, otherwise pages 2+ would vanish on every tick.
	// Pauses when the tab is hidden to avoid browser-throttled background polling.
	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;

		const doRefresh = () => {
			if (AllPostData.length === 0) return;
			const refreshCount = Math.min(AllPostData.length, 100);
			const baseUrl = `${BACKEND_URL}/api/`;
			const endpoint = Topic === 0 ? 'myFeed' : 'myFeedPro';
			const url = `${baseUrl}${endpoint}?limit=${refreshCount}&offset=0${Topic !== 0 ? `&topic=${Topic}` : ''}`;

			fetch(url, {
				credentials: 'include',
			})
				.then((res) => (res.ok ? res.json() : null))
				.then((data) => {
					if (Array.isArray(data)) {
						data.forEach((each) => {
							updatePostMetadata(each.id, {
								likeCount: each.likeCount,
								isLiked: each.isLiked === 1,
								commentCount: each.commentCount,
							});
						});
					}
				})
				.catch((err) => console.error('Silent refresh error:', err));
		};

		const startPolling = () => {
			if (interval) clearInterval(interval);
			// 60s (was 30s): halves the standing feed-refresh load from every open
			// tab. Each tick re-runs a full serialized feed page server-side, so at
			// launch scale the cadence matters more than the freshness delta.
			interval = setInterval(doRefresh, 60000);
		};

		const stopPolling = () => {
			if (interval) {
				clearInterval(interval);
				interval = null;
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden) {
				stopPolling();
			} else {
				// Refresh immediately on re-focus, then resume interval.
				doRefresh();
				startPolling();
			}
		};

		// Start only if tab is currently visible.
		if (!document.hidden) {
			startPolling();
		}
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			stopPolling();
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Topic, AllPostData.length, updatePostMetadata]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
				setOpenShareId(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Auto-dismiss the "post no longer exists" toast.
	useEffect(() => {
		if (!toastMessage) return;
		const t = setTimeout(() => setToastMessage(null), 3000);
		return () => clearTimeout(t);
	}, [toastMessage]);

	// RESTORED: Handle Topic Click
	const handleTopicClick = (category: TopicsType) => {
		if (Topic === category.key) return; // Prevent reload if clicking same topic
		set_Topic(category.key);
		set_AllPostData([]);
		setPage(1);
		setHasMore(true);
		// fetchPosts will be called by the useEffect below
	};

	const navigateToPost = (postId: string) => {
		sessionStorage.setItem('newsScrollPos', window.scrollY.toString());
		sessionStorage.setItem(
			'newsFeedState',
			JSON.stringify({
				Topic: Topic,
				AllPostData: AllPostData,
				page: page,
				hasMore: hasMore,
				isSidebarOpen: isSidebarOpen,
			})
		);
		navigate(`/getpost/${postId}`);
	};

	const formatDateToRelativeTime = (dateString: string) => {
		try {
			const date = parseISO(dateString);
			return formatCustomDistanceToNow(date);
		} catch (e) {
			return dateString;
		}
	};

	const reactPost = async (postId: number, type: string, isAnonymous: boolean) => {
		const requestOptions: RequestInit = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ postId, type, isAnonymous }),
			credentials: 'include',
		};
		try {
			const res = await fetch(`${BACKEND_URL}/api/react`, requestOptions);
			// fetch() doesn't throw on 4xx, so we must inspect the status. A 404
			// here means the post was deleted (e.g. from another device) — signal
			// the caller so it can drop the stale card from the feed.
			if (res.status === 404) return false;
			return true;
		} catch (error) {
			// Network error — keep the post; it's not proof it's gone.
			console.error(error);
			return true;
		}
	};

	// A like landed on a post that no longer exists. Tell the user and refresh
	// the feed by removing the dead card (and its cached state) on the spot.
	const handleDeadPost = (postId: number) => {
		setToastMessage('This post no longer exists.');
		set_AllPostData((prev) => prev.filter((p) => p.id !== postId));
		setLikes((prev) => {
			const next = { ...prev };
			delete next[postId];
			return next;
		});
	};

	const handleLikeClick = (postId: number, isAnonymous: boolean) => {
		const post = AllPostData.find((p) => p.id === postId);
		const currentMetadata = postMetadata[postId];
		const isLiked = currentMetadata?.isLiked ?? likes[postId] ?? post?.isLiked === 1;
		const newIsLiked = !isLiked;
		const newType = newIsLiked ? 'like' : 'dislike';

		setLikes((prevLikes) => ({ ...prevLikes, [postId]: newIsLiked }));

		const currentCount = currentMetadata?.likeCount ?? post?.likeCount ?? 0;
		const newLikeCount = newIsLiked ? currentCount + 1 : Math.max(0, currentCount - 1);

		updatePostMetadata(postId, { isLiked: newIsLiked, likeCount: newLikeCount });

		set_AllPostData((prevPosts) =>
			prevPosts.map((post) => {
				if (post.id === postId) {
					return { ...post, likeCount: newLikeCount };
				}
				return post;
			})
		);
		reactPost(postId, newType, isAnonymous).then((stillExists) => {
			if (!stillExists) handleDeadPost(postId);
		});
	};

	// Load Posts Effect - Runs on Mount AND when Topic changes
	useEffect(() => {
		const savedStateStr = sessionStorage.getItem('newsFeedState');

		if (savedStateStr) {
			try {
				const savedState = JSON.parse(savedStateStr);
				if (savedState.Topic === Topic && savedState.AllPostData && savedState.AllPostData.length > 0) {
					sessionStorage.removeItem('newsFeedState');
					wasRestoredRef.current = true;
					return;
				} else {
					sessionStorage.removeItem('newsFeedState');
				}
			} catch (e) {
				console.error('Error parsing session storage', e);
				sessionStorage.removeItem('newsFeedState');
			}
		}

		setIsLoading(true);
		setPage(1);
		setHasMore(true);
		fetchPosts(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Topic]);

	// Background Refresh on Restore — fires once after a sessionStorage restore
	useEffect(() => {
		if (!wasRestoredRef.current || AllPostData.length === 0) return;
		wasRestoredRef.current = false;

		const refreshCount = Math.min(AllPostData.length, 100);
		const baseUrl = `${BACKEND_URL}/api/`;
		const endpoint = Topic === 0 ? 'myFeed' : 'myFeedPro';
		const url = `${baseUrl}${endpoint}?limit=${refreshCount}&offset=0${Topic !== 0 ? `&topic=${Topic}` : ''}`;

		fetch(url, {
			credentials: 'include',
		})
			.then((res) => res.json())
			.then((data) => {
				if (Array.isArray(data)) {
					data.forEach((each) => {
						updatePostMetadata(each.id, {
							likeCount: each.likeCount,
							isLiked: each.isLiked === 1,
							commentCount: each.commentCount,
						});
					});
				}
			})
			.catch((err) => console.error('Background refresh error:', err));
	}, [AllPostData.length, Topic, updatePostMetadata]);

	// Sort Helper: Newest First
	const sortPostsByDate = (posts: AllPostDataType[]) => {
		return posts.sort((a, b) => {
			return new Date(b.time).getTime() - new Date(a.time).getTime();
		});
	};

	const PAGE_SIZE = 10;

	const fetchPosts = async (targetPage: number) => {
		if (targetPage > 1) setIsFetchingMore(true);

		const myReqId = ++requestIdRef.current;
		const search = activeSearchRef.current;
		const baseUrl = `${BACKEND_URL}/api/`;
		const endpoint = Topic === 0 ? 'myFeed' : 'myFeedPro';
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${baseUrl}${endpoint}?limit=${PAGE_SIZE}&offset=${(targetPage - 1) * PAGE_SIZE}${Topic !== 0 ? `&topic=${Topic}` : ''}${searchParam}`;

		try {
			const response = await fetch(url, {
				credentials: 'include',
				headers: {
				},
			});
			if (!response.ok) throw new Error('API Failed');

			let result: PostType[] = await response.json();

			// Drop stale responses — a newer fetch has superseded this one
			// (e.g. user kept typing in the search box).
			if (myReqId !== requestIdRef.current) return;

			if (result.length < PAGE_SIZE) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}

			if (Array.isArray(result)) {
				result.reverse();
			}

			const processedPosts = result.map((each) => {
				let isImage = false;
				let internalURL = '';
				let mediaFormat = 'unknown';
				let webLink = '';
				let youtubeLink = '';

				if (each.media && each.media.length > 0) {
					if (each.media[0].format && each.media[0].format.substring(0, 5) === 'image') {
						isImage = true;
					}
					mediaFormat = each.media[0].format || 'unknown';
					internalURL = each.media[0].internalURL || '';

					if (each.media[0].link) {
						const url = each.media[0].link;
						const regex =
							/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)/;
						const match = url.match(regex);
						// Only accept http(s) links for the <iframe> embed below — the link is
						// attacker-controlled (set by the post author), so javascript:/data:/
						// intent: etc. must never reach the iframe src. Non-web links are dropped.
						if (match === null) webLink = isHttpUrl(url) ? url : '';
						else youtubeLink = match[1];
					}
				}

				const body = each.body || '';
				const truncatedLength = body.length > 65 ? body.slice(0, 65) + '... ' : body;

				const finalImageUrl = isImage && internalURL ? mediaUrl(internalURL) : Placeholder;

				return {
					id: each.id,
					title: each.title,
					content: body,
					alteredContent: truncatedLength,
					isImage: isImage,
					format: mediaFormat,
					image_url: finalImageUrl,
					media_filename: internalURL,
					name: each.isAnonymous ? 'Anonymous' : each.user?.name || 'Unknown',
					location:
						each.media && each.media.length > 0 ? `(${each.media[0].latitude}, ${each.media[0].longitude})` : '',
					time: `${each.createdAt}`,
					commentCount: each.commentCount || 0,
					likeCount: each.likeCount || 0,
					isLiked: each.isLiked || 0,
					tags: each.tags || '',
					profilepicURL: each.user?.profilepicURL || '',
					webLink: webLink,
					youtubeLink: youtubeLink,
					isAnonymous: each.isAnonymous || false,
					shareHash: each.shareHash,
				} as AllPostDataType;
			});

			const validPosts = processedPosts;

			let finalPosts: AllPostDataType[] = [];
			if (targetPage === 1) {
				finalPosts = sortPostsByDate(validPosts);
			} else {
				// When loading more, we append and then sort
				// We also filter out duplicates just in case some new posts arrived and shifted the offset
				const existingIds = new Set(AllPostData.map((p) => p.id));
				const uniqueNewPosts = validPosts.filter((p) => !existingIds.has(p.id));
				const combined = [...AllPostData, ...uniqueNewPosts];
				finalPosts = sortPostsByDate(combined);
			}

			set_AllPostData(finalPosts);

			const initialLikes = finalPosts.reduce(
				(acc, post) => {
					acc[post.id] = post.isLiked === 1;
					return acc;
				},
				{} as Record<number, boolean>
			);

			setLikes((prevLikes) => ({ ...prevLikes, ...initialLikes }));

			// Update global metadata registry
			finalPosts.forEach((post) => {
				updatePostMetadata(post.id, {
					likeCount: post.likeCount,
					isLiked: post.isLiked === 1,
					commentCount: post.commentCount,
				});
			});
		} catch (error) {
			console.error('Error fetching posts:', error);
		} finally {
			if (targetPage === 1) setIsLoading(false);
			setIsFetchingMore(false);
		}
	};

	const handleLoadMore = () => {
		const nextPage = page + 1;
		setPage(nextPage);
		fetchPosts(nextPage);
	};

	// Infinite scroll via IntersectionObserver.
	// A sentinel element near the end of the list triggers the next page
	// when it scrolls into view (with a 400px rootMargin so we start
	// loading just before the user reaches it).
	useEffect(() => {
		const sentinel = loadMoreSentinelRef.current;
		if (!sentinel) return;
		if (!hasMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && entry.isIntersecting && !isFetchingMore && !isLoading) {
					handleLoadMore();
				}
			},
			{ rootMargin: '400px 0px' }
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
		// AllPostData.length is in the deps so the observer re-binds whenever
		// the sentinel DOM node unmounts/remounts — e.g. when the list goes
		// 0→N after clearing a search filter.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMore, isFetchingMore, isLoading, page, Topic, AllPostData.length]);

	const handleShareButtonClick = (postId: number) => {
		setOpenShareId(openShareId === postId ? null : postId);
	};

	const renderHighlightedText = (text: any, query: string) => {
		const safeText = text ? String(text) : '';
		const trimmedQuery = query.trim();
		if (!trimmedQuery) return <span>{safeText}</span>;
		try {
			const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`(${escapedQuery})`, 'gi');
			const parts = safeText.split(regex);
			return (
				<>
					{parts.map((part, i) =>
						part.toLowerCase() === trimmedQuery.toLowerCase() ? (
							<span key={i} className='bg-blue-200 text-blue-800 rounded px-0.5 font-bold'>
								{part}
							</span>
						) : (
							<span key={i}>{part}</span>
						)
					)}
				</>
			);
		} catch (e) {
			return <span>{safeText}</span>;
		}
	};

	// Minimum query length before we hit the server. A leading-wildcard
	// `LIKE '%x%'` on a 1–2 char term can't use an index (full scan) and
	// matches almost everything, so it's the costliest, least useful query.
	// Below this we treat the box as empty and show the normal feed.
	const MIN_SEARCH_LENGTH = 3;

	// Debounced server-side search.
	// On every keystroke, schedule a refetch 300ms later. If the user keeps
	// typing, the previous timer is cleared and only the final query hits
	// the server. AllPostData is reset so pagination starts fresh for the
	// new query, and `requestIdRef` discards any in-flight stale fetch.
	useEffect(() => {
		// Anything shorter than the minimum counts as "no search".
		const effective = searchQuery.trim().length < MIN_SEARCH_LENGTH ? '' : searchQuery.trim();
		if (isFirstSearchEffect.current) {
			isFirstSearchEffect.current = false;
			activeSearchRef.current = effective;
			return;
		}
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			// Skip the refetch when the effective term hasn't actually changed
			// (e.g. the user is still typing below the threshold).
			if (effective === activeSearchRef.current) return;
			activeSearchRef.current = effective;
			set_AllPostData([]);
			setPage(1);
			setHasMore(true);
			fetchPosts(1);
		}, 300);
		return () => {
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};

	return (
		<div ref={topRef} className='min-h-screen bg-gray-100'>
			{/* Transient toast — e.g. when a liked post no longer exists */}
			{toastMessage && (
				<div className='fixed top-6 left-1/2 -translate-x-1/2 z-[1100] bg-gray-900 text-white px-5 py-3 rounded-xl text-sm font-semibold shadow-2xl'>
					{toastMessage}
				</div>
			)}

			{/* Header */}
			{/* Header */}
			<Header />

			{/* Mobile Overlay */}
			{isSidebarOpen && (
				<div className='fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden' onClick={() => setIsSidebarOpen(false)} />
			)}

			{/* Mobile Filter Button */}
			{!isSidebarOpen && (
				<div className='fixed bottom-20 left-4 z-40 lg:hidden'>
					<button
						onClick={() => setIsSidebarOpen(true)}
						className='bg-[#004a99]/80 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center backdrop-blur-sm'
						style={{ width: '40px', height: '40px' }}
						title='Open filter'
					>
						<Filter className='w-6 h-6' />
					</button>
				</div>
			)}

			{/* Desktop Sidebar Toggle Button */}
			{!isSidebarOpen && (
				<div className='fixed top-24 left-6 z-40 lg:block hidden'>
					<button
						onClick={() => setIsSidebarOpen(true)}
						className='bg-[#004a99] text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group'
						style={{ width: '48px', height: '48px' }}
						title='Open filter'
					>
						<Filter className='w-6 h-6' />
						<span className='absolute left-full ml-3 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none'>
							Category filter
						</span>
					</button>
				</div>
			)}

			{/* Main Container */}
			<div className='pt-[var(--header-height,80px)]'>
				{/* Left Sidebar */}
				<div
					className={`fixed left-0 top-[var(--header-height,80px)] w-80 lg:w-96 h-screen bg-white border-r border-gray-200 overflow-y-auto transition-transform duration-300 z-50 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
				>
					<div className='px-6 pt-8'>
						<div className='relative mb-2'>
							{' '}
							{/* Header Group */}
							<div className='flex justify-between items-center h-9'>
								<h2 className='text-lg font-bold text-gray-900 relative top-[1px] leading-tight'>Category filter</h2>
								<div className='flex items-center gap-2'>
									<button
										className='p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors inline-flex items-center justify-center relative -top-[2px]'
										onClick={() => setIsSidebarOpen(false)}
										title='Collapse sidebar'
									>
										<ChevronLeft className='w-5 h-5' />
									</button>
								</div>
							</div>
						</div>
					</div>
					<div className='pb-4'>
						<div className='mt-0'>
							{/* All Topics Button */}
							<button
								className={`w-full flex items-center px-4 py-2.5 text-left transition-all duration-200 group ${
									Topic === 0
										? 'bg-blue-50 text-blue-700 border-r-3 border-blue-500'
										: 'hover:bg-gray-50 text-gray-700 hover:border-r-3 hover:border-gray-300'
								}`}
								onClick={() => handleTopicClick({ name: 'All Topics', img: '', filter: false, key: 0 })}
							>
								<div
									className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-colors ${
										Topic === 0
											? 'bg-blue-500 text-white'
											: 'bg-gradient-to-br from-blue-500 to-purple-600 text-white group-hover:scale-105'
									}`}
								>
									<span className='text-xs font-bold'>All</span>
								</div>
								<span className='font-medium text-sm'>All Topics</span>
							</button>

							{/* Dynamic Topic Buttons */}
							{Topics.map((category, index) => (
								<button
									key={index}
									className={`w-full flex items-center px-4 py-2.5 text-left transition-all duration-200 group ${
										Topic === category.key
											? 'bg-blue-50 text-blue-700 border-r-3 border-blue-500'
											: 'hover:bg-gray-50 text-gray-700 hover:border-r-3 hover:border-gray-300'
									}`}
									onClick={() => handleTopicClick(category)}
								>
									<div
										className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 overflow-hidden transition-transform ${
											Topic === category.key ? '' : 'group-hover:scale-105'
										}`}
									>
										<img src={category.img} alt={category.name} className='w-6 h-6 object-contain' />
									</div>
									<span className='font-medium text-sm'>{category.name}</span>
								</button>
							))}
						</div>
					</div>
				</div>

				{/* Main Content Area */}
				<div className='flex justify-center'>
					<div className='w-full max-w-2xl px-6 py-6'>
						{/* Title Header */}
						<div className='mb-6'>
							<h1 className='m-0 text-2xl font-bold leading-tight text-gray-900 text-left'>
								{Topic === 0 ? 'General Feed' : Topics.find((t) => t.key === Topic)?.name || 'Feed'}
							</h1>
							<p className='m-0 mt-1 leading-tight text-gray-600 text-left'>
								{Topic === 0 ? 'Latest news from everyone' : `Latest in ${Topics.find((t) => t.key === Topic)?.name}`}
							</p>
						</div>

						{/* Search Bar */}
						<div className='mb-6'>
							<div className='relative'>
								<Search className='absolute left-[10px] top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
								<input
									type='text'
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder='Search by Title or Username'
									className='w-full pl-10 sm:pl-12 pr-14 sm:pr-20 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-full 
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        shadow-sm hover:shadow-md transition-shadow text-gray-900 text-[13px] sm:text-base'
								/>
								{searchQuery && (
									<button
										onClick={() => setSearchQuery('')}
										className='absolute right-[6px] top-1/2 -translate-y-1/2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-500 text-[10px] font-bold rounded-full transition-colors border border-gray-200 flex items-center justify-center leading-none'
										style={{ height: 'calc(100% - 12px)' }}
									>
										Clear
									</button>
								)}
							</div>
						</div>

						{/* Posts Feed */}
						<div className='space-y-4'>
							{isLoading ? (
								<div className='text-center py-12'>
									<div className='text-gray-500'>Loading news...</div>
								</div>
							) : AllPostData.length === 0 ? (
								<div className='text-center py-12'>
									<div className='text-gray-500'>No posts found.</div>
								</div>
							) : (
								AllPostData.map((data) => (
									<article
										key={data.id}
										className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow'
										style={{
											// Skip paint/layout for off-screen cards so the feed reveals
											// top-to-bottom as the user scrolls, even when React mounts
											// a whole batch in one commit. Browsers that don't support
											// this just ignore it.
											contentVisibility: 'auto',
											containIntrinsicSize: 'auto 500px',
										}}
										onClick={() => navigateToPost(data.id.toString())}
									>
										{/* Post Header */}
										<div className='p-2.5 sm:p-4 border-b border-gray-100'>
											{/* Line 1: Author (bigger pfp + name over date) + Risk button,
											    kept vertically centered on this row. */}
											<div className='flex items-center justify-between gap-1.5 sm:gap-3'>
												<div className='flex items-center gap-3 sm:gap-4 min-w-0'>
													<ProfileImage
														name={data.name}
														pfpURL={data.profilepicURL}
														className='w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover border border-gray-200 shrink-0'
														isAnonymous={data.isAnonymous}
													/>
													{/* Name uses the former title size, sitting just above the date.
													    items-start keeps name and date individually left-aligned. */}
													<div className='flex flex-col items-start min-w-0'>
														<span className='font-bold text-gray-900 text-sm sm:text-xl leading-tight truncate text-left'>
															{renderHighlightedText(data.name, searchQuery)}
														</span>
														<div className='flex items-center text-[10px] sm:text-xs text-gray-500 gap-1 sm:gap-1.5'>
															<Clock className='w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 shrink-0' />
															<span className='truncate'>
																{(() => {
																	try {
																		const date = parseISO(data.time);
																		const distancePart = formatCustomDistanceToNow(date);
																		return distancePart;
																	} catch (e) {
																		return formatDateToRelativeTime(data.time);
																	}
																})()}
															</span>
														</div>
													</div>
												</div>
												{!data.youtubeLink && (
													<button
														className='flex items-center gap-1 px-1.5 py-0.5 sm:gap-1.5 sm:px-3 sm:py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-[10px] sm:text-sm font-semibold border border-amber-100 shrink-0 self-center'
														onClick={(e) => {
															e.stopPropagation();
															if (data.media_filename) {
																setRiskoMeterMediaId(data.media_filename);
															}
														}}
														title='Risko-Meter'
													>
														<Gauge className='w-3 h-3 sm:w-4 sm:h-4' />
														<span>Risk</span>
													</button>
												)}
											</div>

											{/* Line 2: Title — now uses the former, smaller username size. */}
											<h2 className='text-[10px] sm:text-sm font-semibold text-gray-900 text-left truncate mt-3 sm:mt-4' title={data.title || ''}>
												{renderHighlightedText(data.title, searchQuery)}
											</h2>

											{/* Line 3: Description preview — replaces the tags row visually.
											    Tags are still kept on the post object so filtering works. */}
											{data.content && data.content.trim() !== '' && (
												<CardDescription text={data.content.trim()} />
											)}
										</div>

										{/* Post Media */}
										{data.isImage && (
											<div className='relative'>
												<PostImage
													src={data.image_url}
													alt='Post'
													className='w-full h-auto max-h-96 object-cover bg-gray-100'
												/>
											</div>
										)}

										{/* YouTube/Web Content */}
										{data.youtubeLink && (
											<div className='px-4 my-3'>
												<div className='aspect-video rounded-lg overflow-hidden'>
													<iframe
														src={`https://www.youtube.com/embed/${data.youtubeLink}`}
														className='w-full h-full'
														frameBorder='0'
														title='YouTube video'
														allowFullScreen
														allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
													/>
												</div>
											</div>
										)}

										{data.webLink && (
											<div className='px-4 my-3'>
												<div className='aspect-video rounded-lg overflow-hidden border border-gray-200'>
													<iframe
														src={data.webLink}
														className='w-full h-full'
														title={data.title}
														sandbox='allow-scripts allow-same-origin allow-popups allow-forms allow-presentation'
														referrerPolicy='no-referrer'
														allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
													/>
												</div>
											</div>
										)}

										{/* Action Buttons (Footer) */}
										<div className='h-8 sm:h-12 shrink-0'>
											<div className='flex items-center justify-between h-full'>
												{/* Like Region */}
												<button
													onClick={(e) => {
														e.stopPropagation();
														handleLikeClick(data.id, data.name === 'Anonymous');
													}}
													className={`flex-1 flex items-center justify-center h-full transition-colors ${
														(postMetadata[data.id]?.isLiked ?? likes[data.id])
															? 'text-blue-600 bg-blue-50/50 hover:bg-blue-50'
															: 'text-gray-600 hover:bg-gray-50'
													} border-t border-r border-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black`}
												>
													<div className='flex items-center relative'>
														{(postMetadata[data.id]?.likeCount ?? data.likeCount) > 0 && (
															<span className='absolute right-full mr-1 sm:mr-2 font-medium text-[10px] sm:text-sm whitespace-nowrap'>
																{postMetadata[data.id]?.likeCount ?? data.likeCount}
															</span>
														)}
														<Heart
															className={`w-3 h-3 sm:w-5 sm:h-5 transition-all ${
																(postMetadata[data.id]?.isLiked ?? likes[data.id]) ? 'fill-current scale-110' : 'scale-100'
															}`}
														/>
														<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Like</span>
													</div>
												</button>

												{/* Comment Region */}
												<button
													type='button'
													className='flex-1 flex items-center justify-center h-full text-gray-600 hover:bg-gray-50 border-t border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black'
												>
													<div className='flex items-center relative'>
														{(postMetadata[data.id]?.commentCount ?? data.commentCount) > 0 && (
															<span className='absolute right-full mr-1 sm:mr-2 font-medium text-[10px] sm:text-sm whitespace-nowrap'>
																{postMetadata[data.id]?.commentCount ?? data.commentCount}
															</span>
														)}
														<MessageCircle className='w-3 h-3 sm:w-5 sm:h-5' />
														<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Comment</span>
													</div>
												</button>

												{/* Share Region */}
												<div className='flex-1 relative h-full' ref={openShareId === data.id ? shareDropdownRef : null}>
													<button
														onClick={(e) => {
															e.stopPropagation();
															handleShareButtonClick(data.id);
														}}
														className='w-full h-full flex items-center justify-center space-x-1 sm:space-x-2 text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black'
													>
														<Share2 className='w-3 h-3 sm:w-5 sm:h-5' />
														<span className='font-medium text-[10px] sm:text-sm'>Share</span>
													</button>
													{openShareId === data.id && (
														<div
															className='absolute right-0 bottom-full mb-2 z-50'
															onClick={(e) => e.stopPropagation()}
														>
															<SharingTab url={`${WEB_BASE_URL}/share/${data.id}/${data.shareHash}`} title={data.title} />
														</div>
													)}
												</div>
											</div>
										</div>
									</article>
								))
							)}

							{/* Infinite scroll sentinel — IntersectionObserver triggers next page when this scrolls into view. */}
							{hasMore && AllPostData.length > 0 && (
								<div ref={loadMoreSentinelRef} className='pt-6 pb-12 flex justify-center'>
									{isFetchingMore ? (
										<div className='flex items-center gap-2 text-gray-500'>
											<div className='w-5 h-5 border-2 border-gray-300 border-t-[#022c56] rounded-full animate-spin'></div>
											<span>Loading more...</span>
										</div>
									) : (
										<button
											onClick={handleLoadMore}
											aria-label='Load more stories'
											className='px-4 py-2 text-xs text-gray-400 hover:text-gray-600 rounded-full border border-gray-200 hover:border-gray-300'
										>
											Load more
										</button>
									)}
								</div>
							)}

							{!hasMore && AllPostData.length > 0 && (
								<div className='py-12 text-center'>
									<div className='inline-block px-4 py-2 bg-gray-50 rounded-full border border-gray-100 text-gray-400 text-sm font-medium'>
										You've reached the end of the news feed
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{riskoMeterMediaId && <RiskoMeter mediaId={riskoMeterMediaId} onClose={() => setRiskoMeterMediaId(null)} />}

			{/* Floating "Go to Top" Button */}
			<div
				className={`go-to-top-button-wrap ${showScrollToOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-[120px] pointer-events-none'
					}`}
			>
				<button
					onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
					className='go-to-top-button bg-blue-600 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all'
					title='Go to Top'
				>
					<ArrowUp size={24} />
				</button>
			</div>
		</div>
	);
}

export default MyNewsFeed;
